import {
  Autocomplete,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  InputAdornment,
  Paper,
  TextField,
  Typography,
} from "@mui/material";
import { useDispatch } from "react-redux";
import {
  setSelectedDestinationTableCreateScreen,
  setSelectedInterfaceCreateScreen,
  setUniqueDefinitionData,
} from "../../../../../features/uniqueDefSlice";
import AddIcon from "@mui/icons-material/Add";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Grid, Stack } from "@mui/material";
import { FixedSizeList, ListChildComponentProps } from "react-window";
import React from "react";
import { useSelector } from "react-redux";
import SearchIcon from "@mui/icons-material/Search";

interface CreateNewDefinitionDialogProps {
  open: boolean;
  onClose: () => void;
}

const CreateNewDefinitionDialog = ({
  open,
  onClose,
}: CreateNewDefinitionDialogProps) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const interfaceId = searchParams.get("interfaceId");
  const interfaceName = searchParams.get("interfaceName");
  const destinationTableId = searchParams.get("destinationTableId");
  const destinationTableName = searchParams.get("destinationTableName");
  const handleCreateNewDefinition = () => {
    dispatch(setUniqueDefinitionData([]));
    navigate(
      `/data_collection/unifier/unique_definitions/create_new_definition?interfaceId=${interfaceId}&interfaceName=${interfaceName}&destinationTableId=${destinationTableId}&destinationTableName=${destinationTableName}`
    );
  };

  const {
    interfaceData,
    destinationTablesData,
    selectedInterfaceCreateScreen,
    selectedDestinationTableCreateScreen,
  } = useSelector((state: any) => state.uniqueDefinition);

  const LISTBOX_PADDING = 8;
  const renderRow = (props: ListChildComponentProps) => {
    const { data, index, style } = props;
    const option = data[index];
    return React.cloneElement(option, {
      style: {
        ...style,
        top: (style.top as number) + LISTBOX_PADDING,
      },
    });
  };
  const VirtualizedListboxComponent = React.forwardRef<
    HTMLDivElement,
    React.HTMLAttributes<HTMLElement>
  >(function ListboxComponent(props, ref) {
    const { children, ...other } = props;
    const itemCount = Array.isArray(children) ? children.length : 0;
    const itemSize = 36; // item height in px, adjust to your needs
    const height = Math.min(8, itemCount) * itemSize + 2 * LISTBOX_PADDING;

    return (
      <div ref={ref} {...other}>
        <FixedSizeList
          height={height}
          width="100%"
          itemSize={itemSize}
          itemCount={itemCount}
          overscanCount={5}
          itemData={children}
          style={{ padding: LISTBOX_PADDING }}
        >
          {renderRow}
        </FixedSizeList>
      </div>
    );
  });
  const selectedInterfaceValue = selectedInterfaceCreateScreen
    ? {
        label: selectedInterfaceCreateScreen.affiliate_system_interface_name,
        id: selectedInterfaceCreateScreen.id,
      }
    : null;

  const selectedDestinationTableValue = selectedDestinationTableCreateScreen
    ? {
        label: selectedDestinationTableCreateScreen.destination_table,
        id: selectedDestinationTableCreateScreen.id,
      }
    : null;
  const handleInterfaceChange = (_: any, value: any, reason: string) => {
    const selectedTable = value
      ? interfaceData?.find((item: any) => item.id === value.id)
      : null;

    dispatch(setSelectedInterfaceCreateScreen(selectedTable));

    // Update URL parameters
    const updatedParams = new URLSearchParams(searchParams);
    if (selectedTable) {
      updatedParams.set("interfaceId", selectedTable.id.toString());
      updatedParams.set(
        "interfaceName",
        selectedTable.affiliate_system_interface_name
      );
    } else {
      updatedParams.delete("interfaceId");
      updatedParams.delete("interfaceName");
    }
    setSearchParams(updatedParams);

    if (reason === "clear") {
      dispatch(setUniqueDefinitionData([]));
    }
  };

  const handleDestinationTableChange = (_: any, value: any, reason: string) => {
    const selectedTable = value
      ? destinationTablesData?.find((item: any) => item.id === value.id)
      : null;

    dispatch(setSelectedDestinationTableCreateScreen(selectedTable));

    // Update URL parameters
    const updatedParams = new URLSearchParams(searchParams);
    if (selectedTable) {
      updatedParams.set("destinationTableId", selectedTable.id.toString());
      updatedParams.set(
        "destinationTableName",
        selectedTable.destination_table
      );
    } else {
      updatedParams.delete("destinationTableId");
      updatedParams.delete("destinationTableName");
    }
    setSearchParams(updatedParams);

    if (reason === "clear") {
      dispatch(setUniqueDefinitionData([]));
    }
  };
  return (
    <Dialog
      open={open}
      onClose={(_, reason) => {
        if (reason !== "backdropClick") onClose();
      }}
      fullWidth
      maxWidth="md"
    >
      <DialogTitle>Create New Definition</DialogTitle>
      <DialogContent>
        <Paper elevation={0} sx={{ padding: "12px 0px" }}>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <Stack spacing={1}>
                <Autocomplete
                  value={selectedInterfaceValue?.label || null}
                  ListboxComponent={VirtualizedListboxComponent}
                  options={
                    interfaceData?.map((item: any) => ({
                      label: item.affiliate_system_interface_name,
                      id: item.id,
                    })) || []
                  }
                  onChange={handleInterfaceChange}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      placeholder="Choose from the list"
                      label="Interface"
                      required
                      InputProps={{
                        ...params.InputProps,
                        startAdornment: (
                          <InputAdornment position="start">
                            <SearchIcon sx={{ marginLeft: "12px" }} />
                          </InputAdornment>
                        ),
                      }}
                      color="secondary"
                      size="small"
                    />
                  )}
                />
                <Typography
                  sx={{
                    fontSize: "12px",
                    color: "#00000099",
                    paddingLeft: "12px",
                  }}
                >
                  All fields are mandatory
                </Typography>
              </Stack>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Stack spacing={1}>
                <Autocomplete
                  value={selectedDestinationTableValue?.label || null}
                  ListboxComponent={VirtualizedListboxComponent}
                  options={
                    destinationTablesData?.map((item: any) => ({
                      label: item.destination_table,
                      id: item.id,
                    })) || []
                  }
                  onChange={handleDestinationTableChange}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      placeholder="Choose from the list"
                      label="Destination Table"
                      required
                      InputProps={{
                        ...params.InputProps,
                        startAdornment: (
                          <InputAdornment position="start">
                            <SearchIcon sx={{ marginLeft: "12px" }} />
                          </InputAdornment>
                        ),
                      }}
                      color="secondary"
                      size="small"
                    />
                  )}
                />
                <Typography
                  sx={{
                    fontSize: "12px",
                    color: "#00000099",
                    paddingLeft: "12px",
                  }}
                >
                  All fields are mandatory
                </Typography>
              </Stack>
            </Grid>
          </Grid>
        </Paper>
      </DialogContent>
      <DialogActions sx={{ px: 2, pb: 2 }}>
        <Button onClick={onClose} color="secondary">
          Cancel
        </Button>
        <Button
          variant="contained"
          color="secondary"
          onClick={handleCreateNewDefinition}
          disabled={!selectedDestinationTableValue || !selectedInterfaceValue}
        >
          Create Definition
          <AddIcon
            sx={{ width: "20px", ml: "5px", mb: "2px" }}
            color="inherit"
          />
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CreateNewDefinitionDialog;
